import { LIST_QUERY_GROUPS_NAME } from "@/lib/ai/tools/list-query-groups/constants";
import type { ListQueryGroupsOutput } from "@/lib/ai/tools/list-query-groups/server";
import { defineDatasetSpec } from "@/lib/ai/visualization/data/adapters/adapter-contract";
import {
  buildListTableDataset,
  createTableOnlyProfile,
  toListNumber,
  toListText,
  toListTextList,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type {
  VizDataset,
  VizDatasetRow,
} from "@/lib/ai/visualization/data/schema";

const QUERY_GROUPS_DATASET_SPEC = defineDatasetSpec({
  id: `${LIST_QUERY_GROUPS_NAME}-table`,
  profile: createTableOnlyProfile({ datasetFamily: "list.queryGroups" }),
  fields: [
    { key: "projectType", role: "dim", type: "string" },
    { key: "searchAnalyticsId", role: "dim", type: "string" },
    { key: "queryGroupId", role: "dim", type: "string" },
    { key: "queryGroupName", role: "dim", type: "string" },
    { key: "description", role: "dim", type: "string" },
    { key: "operator", role: "dim", type: "string" },
    { key: "patternCount", role: "metric", type: "number", unit: "count" },
    { key: "patternsPreview", role: "dim", type: "string" },
  ] as const,
  defaults: { category: "queryGroupName", value: "patternCount" },
  notes: "Table dataset normalized from list_query_groups output.",
});

function toQueryGroupRows(
  output: Partial<ListQueryGroupsOutput>,
): VizDatasetRow[] {
  const groups = Array.isArray(output.queryGroups) ? output.queryGroups : [];

  return groups.map((group) => {
    const patterns = Array.isArray(group.patterns) ? group.patterns : [];
    const normalizedPatterns = patterns.filter(
      (pattern): pattern is NonNullable<typeof pattern> => pattern != null,
    );
    return {
      projectType: toListText(output.projectType),
      searchAnalyticsId: toListText(output.searchAnalyticsId),
      queryGroupId: toListText(group.id),
      queryGroupName: toListText(group.name),
      description: toListText(group.description),
      operator: toListText(group.op),
      patternCount: toListNumber(normalizedPatterns.length),
      patternsPreview: toListTextList(
        normalizedPatterns.map(
          (pattern) => `${pattern.type}:${pattern.pattern}`,
        ),
      ),
    };
  });
}

/**
 * list_query_groups 出力を table-only dataset へ正規化する。
 */
export function buildListQueryGroupsDatasets(
  output: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  const typedOutput =
    typeof output === "object" && output !== null
      ? (output as Partial<ListQueryGroupsOutput>)
      : {};
  const rows = toQueryGroupRows(typedOutput);
  const dataset = buildListTableDataset({
    id: QUERY_GROUPS_DATASET_SPEC.id,
    datasetFamily: QUERY_GROUPS_DATASET_SPEC.profile.datasetFamily,
    variant: QUERY_GROUPS_DATASET_SPEC.profile.variant,
    source,
    rows,
    fields: QUERY_GROUPS_DATASET_SPEC.fields,
    defaults: QUERY_GROUPS_DATASET_SPEC.defaults,
    notes: QUERY_GROUPS_DATASET_SPEC.notes,
  });

  return dataset ? [dataset] : [];
}
